import { useTranslation } from 'react-i18next';
import { CheckSquare, TrendingUp, DollarSign, BarChart2 } from 'lucide-react';
import { KpiCard } from './KpiCard';
import { PeriodSelector } from './PeriodSelector';
import { formatCurrency } from '../utils';
import type { PerformanceSummary, PeriodType } from '../types';

interface KpiSectionProps {
  summary: PerformanceSummary | undefined;
  isLoading: boolean;
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  onNavigate: (direction: 'prev' | 'next') => void;
  onChangePeriodType: (type: PeriodType) => void;
}

export function KpiSection({
  summary,
  isLoading,
  periodType,
  periodStart,
  periodEnd,
  onNavigate,
  onChangePeriodType,
}: KpiSectionProps) {
  const { t } = useTranslation('laborPerformance');

  const deviationPositive = (summary?.deviation ?? 0) >= 0;
  const deviationPrefix = deviationPositive ? '+' : '';

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <span className="text-sm font-semibold">{t('period.sectionTitle')}</span>
        <PeriodSelector
          periodType={periodType}
          periodStart={periodStart}
          periodEnd={periodEnd}
          onNavigate={onNavigate}
          onChangePeriodType={onChangePeriodType}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          data-testid="labor-performance-kpi-total-tasks"
          icon={<CheckSquare className="h-5 w-5" />}
          label={t('kpi.totalTasks')}
          value={String(summary?.totalTasks ?? 0)}
          sublabel={t('kpi.activeMembersLabel', { count: summary?.activeMembersCount ?? 0 })}
          isLoading={isLoading}
        />
        <KpiCard
          data-testid="labor-performance-kpi-avg-efficiency"
          icon={<TrendingUp className="h-5 w-5" />}
          label={t('kpi.averageEfficiency')}
          value={`${summary?.averageEfficiency ?? 0}%`}
          sublabel={t(`kpi.level.optimal`)}
          variant="success"
          isLoading={isLoading}
        />
        <KpiCard
          data-testid="labor-performance-kpi-real-cost"
          icon={<DollarSign className="h-5 w-5" />}
          label={t('kpi.realCost')}
          value={formatCurrency(summary?.realCost ?? 0)}
          sublabel={t('kpi.estimatedLabel', { value: formatCurrency(summary?.estimatedCost ?? 0) })}
          isLoading={isLoading}
        />
        <KpiCard
          data-testid="labor-performance-kpi-deviation"
          icon={<BarChart2 className="h-5 w-5" />}
          label={t('kpi.deviation')}
          value={`${deviationPrefix}${formatCurrency(summary?.deviation ?? 0)}`}
          sublabel={deviationPositive ? t('kpi.savingsLabel') : t('kpi.overrunLabel')}
          variant={deviationPositive ? 'positive' : 'negative'}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
